// src/features/estoque/components/DashboardEstoque.jsx
import React, { useState } from "react";
import { useResumoEstoque } from "../hooks/useResumoEstoque";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "components/ui/card";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  DollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  BarChart3,
  TrendingUp,
} from "lucide-react";

export const DashboardEstoque = ({ defaultMostrarGrafico = true }) => {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mostrarGrafico, setMostrarGrafico] = useState(defaultMostrarGrafico);

  const { dadosGrafico, totais, isLoading } = useResumoEstoque(mes, ano);

  return (
    <Card className="border-gray-200 dark:border-border overflow-hidden shadow-sm">
      <CardHeader className="p-4 sm:p-5 pb-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="space-y-0.5">
            <CardTitle className="text-base sm:text-lg font-bold flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                <DollarSign className="w-4 h-4" />
              </div>
              <span>Resumo Financeiro de Estoque</span>
            </CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Valores contabilizados em Compras (Entradas) e Vendas (NFC-e) via Notas Fiscais.
            </CardDescription>
          </div>

          {/* Filtros de Mês/Ano e Toggle de Gráfico */}
          <div className="flex items-center gap-2 self-stretch sm:self-auto justify-between sm:justify-end">
            <div className="flex items-center gap-2">
              <select
                id="mesFiltro"
                value={mes}
                onChange={(e) => setMes(Number(e.target.value))}
                className="h-8 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 text-xs font-medium text-slate-700 dark:text-slate-200 shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const data = new Date(2000, i, 1);
                  return (
                    <option key={i} value={i} className="dark:bg-slate-900">
                      {data.toLocaleString("pt-BR", { month: "short" })}
                    </option>
                  );
                })}
              </select>

              <Input
                id="anoFiltro"
                type="number"
                value={ano}
                onChange={(e) => setAno(Number(e.target.value))}
                className="h-8 w-20 text-xs text-center font-medium bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setMostrarGrafico(!mostrarGrafico)}
              className={`h-8 text-xs px-2.5 flex items-center gap-1.5 transition-colors ${
                mostrarGrafico
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                  : "text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              <span>{mostrarGrafico ? "Ocultar Gráfico" : "Ver Gráfico"}</span>
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 sm:p-5 pt-0 space-y-4">
        {/* Totalizadores Superiores em Cards Modernos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Total de Compras */}
          <div className="bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/20 rounded-xl p-3.5 flex items-center justify-between gap-3 shadow-sm">
            <div className="min-w-0">
              <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block">
                Total de Compras (Entradas)
              </span>
              <h3
                className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 truncate font-mono"
                title={totais.entradas.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              >
                {totais.entradas.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/30">
              <ArrowDownCircle className="w-5 h-5" />
            </div>
          </div>

          {/* Total de Vendas */}
          <div className="bg-gradient-to-br from-orange-500/10 via-orange-500/5 to-transparent border border-orange-500/20 rounded-xl p-3.5 flex items-center justify-between gap-3 shadow-sm">
            <div className="min-w-0">
              <span className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider block">
                Total de Vendas Registradas
              </span>
              <h3
                className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 truncate font-mono"
                title={totais.saidas.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              >
                {totais.saidas.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center shrink-0 border border-orange-500/30">
              <ArrowUpCircle className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Gráfico Recharts Compacto e Otimizado */}
        {mostrarGrafico && (
          <div className="bg-slate-50/70 dark:bg-slate-950/60 border border-slate-200/90 dark:border-slate-800/80 rounded-xl p-3.5 sm:p-4 pt-3 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                Comparativo Compras vs Vendas no Mês
              </span>
            </div>

            <div className="h-[180px] w-full">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-xs text-muted-foreground animate-pulse">
                    Carregando dados financeiros...
                  </p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dadosGrafico}
                    margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                    barSize={44}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#94a3b8"
                      strokeOpacity={0.25}
                    />
                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tick={{ fill: "currentColor", fontSize: 12, fontWeight: 600 }}
                      className="text-slate-700 dark:text-slate-300"
                    />
                    <YAxis
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(valor) => `R$ ${valor / 1000}k`}
                      tick={{ fill: "currentColor", fontSize: 11 }}
                      className="text-slate-500 dark:text-slate-400"
                    />
                    <RechartsTooltip
                      cursor={{ fill: "currentColor", className: "text-slate-200/40 dark:text-white/5" }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0];
                          return (
                            <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-lg p-2.5 shadow-lg text-xs">
                              <span className="text-slate-500 dark:text-slate-400 block font-medium">
                                {data.payload.name}
                              </span>
                              <span className="font-bold text-slate-900 dark:text-white font-mono text-sm">
                                {data.value.toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                      {dadosGrafico.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardEstoque;
